import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { Map } from 'immutable';
import { push } from 'connected-react18-router';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { getChanged } from '../../utils/ChangeSpy';
import ContactLP from '../../components/Contact/ContactLP';
import { loadContactLP, changeField, findUsers, saveContactLp, clearSuggest } from '../../actions/contact/contactLP';
import AutoCompleteContainer from '../AutoComplete';
import { reloadContactDetail } from '../../actions/contactDetail';
import { usePopupContext } from '../../contexts/PopupContext';
import { useEntityEventContext } from '../../contexts/EntityEventContext';

const ContactLPContainer = ({
  isLP,
  contactLP,
  match,
  loading,
  push,
  loadContactLP,
  changeField,
  findUsers,
  saveContactLp,
}) => {
  const popupContext = usePopupContext();
  const { addOnSaveCallback, removeOnSaveCallback } = useEntityEventContext();

  const handleSubmit = useCallback(event => {
    event.preventDefault();
  }, []);

  const handleBlur = useCallback(() => {
    const body = getChanged(contactLP);
    const { contactId } = match.params;

    if (Object.keys(body).length === 0) return;

    saveContactLp({ contactId, ...body });
  }, [contactLP, match, saveContactLp]);

  const checkIfLp = useCallback(() => {
    const { hasPopup, openPopup, onClosePopup } = popupContext;

    if (hasPopup()) return;
    if (loading) return;

    const { contactId } = match.params;
    let name;

    if (!isLP) {
      if (!isNaN(contactId)) name = 'LPRolePopup';
      else name = 'AddLpContactPopup';
      openPopup(name, { contactId });
      onClosePopup(() => {
        const { contactId } = match.params;

        if (!isLP || contactId !== 'new') {
          return push(`/contact/${contactId}`);
        }
      });
    } else {
      loadContactLP({ id: contactId });
    }
  }, [popupContext, loading, isLP, match, push, loadContactLP]);

  useEffect(() => {
    checkIfLp();
    addOnSaveCallback(handleBlur);

    return () => {
      removeOnSaveCallback(handleBlur);
    };
  }, []);

  useEffect(() => {
    if (loading) checkIfLp();
  }, [loading]);

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      return changeField({ name, value });
    },
    [changeField],
  );

  if (!isLP) {
    return null;
  }

  const suggestRelation = (
    <AutoCompleteContainer
      idName="lpPrimaryRelId"
      onChange={changeField}
      onFind={findUsers}
      rootPath={['contact', 'lp']}
      suggestionsName="suggests"
      valueName="suggestRelation"
    />
  );

  const lpLoading = contactLP.get('loading');

  return (
    <ContactLP
      changeField={changeField}
      contactLP={contactLP}
      findUsers={findUsers}
      isLP={isLP}
      loading={lpLoading}
      match={match}
      onBlur={handleBlur}
      onChange={handleChange}
      onSubmit={handleSubmit}
      suggestRelation={suggestRelation}
    />
  );
};

ContactLPContainer.propTypes = {
  changeField: PropTypes.func.isRequired,
  contactLP: PropTypes.instanceOf(Map).isRequired,
  loadContactLP: PropTypes.func.isRequired,
};

ContactLPContainer.defaultProps = {
  loading: true,
};

const isLp = createSelector(
  state => state.contact.info,
  info => info.get('isLP'),
);

function mapStateToProps(state) {
  return {
    isLP: isLp(state),
    contactLP: state.contact.lp,
  };
}

export { ContactLPContainer };
export default connect(
  mapStateToProps,
  {
    loadContactLP,
    saveContactLp,
    changeField,
    reloadContactDetail,
    findUsers,
    clearSuggest,
    push,
  },
  mergeProps,
  connectOptions,
)(memo(ContactLPContainer));
